import { type Link } from 'types/Link';
import { type S3UploadOutputType } from 'hooks/useUploadFileToS3';
import { type MutationFunctionOptions, type OperationVariables } from '@apollo/client';
import { type ExecutionResult } from 'graphql';
import { type StateSetter } from 'types/utils/React';
import { getDataURLFromFile } from 'utils/file';
import { resizeDataURL } from 'utils/image';
import { POSTCARD_IMAGE_SIZE } from './Front';
import { FormattedMessage } from 'react-intl';
import { type KeyboardEvent, type MouseEventHandler } from 'react';
import { trackEvent, GACommonEventAction, GAEventCategory} from 'services/googleAnalytics';
import { type SendPostcardOutput } from 'graphql/communicate/postcard/PostcardMutations';
import PostcardPlaceholderImage from 'assets/images/postcard/postcard_placeholder.png';
import { getLineCount } from 'utils/string';
import {type ShowSnackbarFunction} from 'hooks';

export const POSTCARD_CONTENT_MAX_LENGTH = 400;
export const POSTCARD_CONTENT_MAX_CHARS_PER_LINE = 45;
export const POSTCARD_CONTENT_MAX_ROWS = 14;

export type GetSendPostcardFunctionInputType = {
  postcardContent: string;
  s3UploadedImage: S3UploadOutputType | undefined;
  selectedLink: Link;
  sendPostcardMutation: (
    options?:
      | MutationFunctionOptions<SendPostcardOutput, OperationVariables>
      | undefined,
  ) => Promise<ExecutionResult<SendPostcardOutput>>;
  setImagePreview: (imagePreview: string) => void;
  setPostcardContent: (content: string) => void;
  setSending: StateSetter<boolean>;
  errorSnackbar: ShowSnackbarFunction;
  successSnackbar: ShowSnackbarFunction;
  setS3UploadedImage: (s3UploadedImage: S3UploadOutputType | undefined) => void;
};

export const getSendPostcardFunction =
  ({
    postcardContent,
    s3UploadedImage,
    selectedLink,
    sendPostcardMutation,
    setImagePreview,
    setPostcardContent,
    setSending,
    errorSnackbar,
    successSnackbar,
    setS3UploadedImage,
  }: GetSendPostcardFunctionInputType): MouseEventHandler<HTMLButtonElement> =>
  (): void => {
    trackEvent(GAEventCategory.POSTCARD, GACommonEventAction.CLICK, 'Envoyer');

    const blobPath = s3UploadedImage?.key;

    if (blobPath) {
      const variables = {
        input: {
          blobPath: blobPath,
          channel: selectedLink?.channelKey,
          text: postcardContent,
        },
      };

      const resetPostcardInterface = () => {
        setImagePreview(PostcardPlaceholderImage as string);
        setPostcardContent('');
        setS3UploadedImage(undefined);
        setSending(false);
      };

      setSending(true);

      sendPostcardMutation({ variables })
        .then(({ data }) => {
          if (!data.sendPostcard.errorReason && data.sendPostcard.sent) {
            trackEvent(
              GAEventCategory.POSTCARD,
              GACommonEventAction.SEND,
              'Envoyer une carte postale',
            );
            successSnackbar(<FormattedMessage id="postcard.sent" />);
          } else {
            errorSnackbar(<FormattedMessage id="postcard.tryLater" />);
          }

          resetPostcardInterface();
        })
        .catch((): void => {
          trackEvent(
            GAEventCategory.POSTCARD,
            GACommonEventAction.ERROR,
            "Erreur d'envoi de carte postale",
          );

          errorSnackbar(<FormattedMessage id="postcard.saveError" />);

          resetPostcardInterface();
        });
    }
  };

export type HandleDropInputType = {
  acceptedFiles: File[];
  isSmallScreen: boolean;
  setImagePreview: StateSetter<string>;
  setShowCrop: StateSetter<boolean>;
};

export const handleDrop = async ({
  acceptedFiles,
  isSmallScreen,
  setImagePreview,
  setShowCrop,
}: HandleDropInputType): Promise<string | undefined> => {
  const file = acceptedFiles[0];

  if (file) {
    const imageDataUrl = await getDataURLFromFile(file);
    const resizedImageDataUrl = await resizeDataURL({
      dataURL: imageDataUrl,
      targetHeight: POSTCARD_IMAGE_SIZE.HEIGHT,
      targetWidth: POSTCARD_IMAGE_SIZE.WIDTH,
    });

    setImagePreview(resizedImageDataUrl);

    if (!isSmallScreen) {
      setShowCrop(true);
    }

    return Promise.resolve(resizedImageDataUrl);
  }

  return Promise.resolve(undefined);
};

type GetHandleShowFrontChangeFunctionInputType = {
  showFront: boolean;
  setShowFront: StateSetter<boolean>;
};

export const getHandleShowFrontChangeFunction =
  ({ showFront, setShowFront }: GetHandleShowFrontChangeFunctionInputType) =>
  (): void => {
    trackEvent(
      GAEventCategory.POSTCARD,
      GACommonEventAction.CLICK,
      'Retourner la carte',
    );
    setShowFront(!showFront);
  };

type GetOnKeyPressFunctionInputType = {
  postcardContent: string;
};

export const getOnKeyPressFunction =
  ({ postcardContent }: GetOnKeyPressFunctionInputType) =>
  (e: KeyboardEvent): void | boolean => {
    const keyCode = e.keyCode || e.which;
    const lineBreaks = postcardContent.split('\n');
    const lines = lineBreaks.reduce((acc: number, value: string) => {
      const lineCount = getLineCount(
        value,
        POSTCARD_CONTENT_MAX_CHARS_PER_LINE,
      );
      return acc + lineCount;
    }, 0);

    // If max number of lines reached and pressed key is new line or number of lines is higher than expected
    if (
      (keyCode === 13 && lines === POSTCARD_CONTENT_MAX_ROWS) ||
      lines > POSTCARD_CONTENT_MAX_ROWS
    ) {
      e.preventDefault();
      return false;
    }
  };
